import { useCallParticipants } from '../../useCallParticipants';
import { useEffect, useRef } from 'react';
import type { TransitionFunc } from '../useCall.types';
import { CallTransition } from '../../../features/call/call.types';
import { useAppSelector } from '../../useReduxToolkit';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { RealtimeRole } from '@doxyme/realtime';

export const useCallStateWaitingParticipants = (
  transition: TransitionFunc,
): (() => Promise<boolean>) => {
  const { role } = useAppSelector(getApolloSessionDetails);

  const { callParticipants } = useCallParticipants();
  const lastParticipantsRef = useRef(callParticipants);

  // JOINED/REMOVED Participants Detection
  useEffect(() => {
    const lastParticipants = lastParticipantsRef.current;

    const newParticipantsWithUsername = callParticipants
      .filter((participant) => Boolean(participant.username))
      .filter(
        ({ id }) =>
          !lastParticipants
            .filter((participant) => Boolean(participant.username))
            .some((p) => p.id === id),
      );

    const newParticipants = callParticipants.filter(
      ({ id }) => !lastParticipants.some((p) => p.id === id),
    );

    const removedParticipants = lastParticipants.filter(
      ({ id }) => !callParticipants.some((p) => p.id === id),
    );

    /**
     * First, if we're a patient, check if the provider left.
     */
    if (
      role === RealtimeRole.Patient &&
      removedParticipants.some(
        (cp) => cp.identity?.role === RealtimeRole.Provider,
      )
    ) {
      void transition(CallTransition.PROVIDER_LEFT);
      lastParticipantsRef.current = callParticipants;
      return;
    }

    if (removedParticipants[0] && callParticipants.length === 0) {
      void transition(CallTransition.LAST_PATIENT_LEFT, {
        participant: removedParticipants[0],
      });
      lastParticipantsRef.current = callParticipants;
      return;
    }

    for (const participant of newParticipants) {
      void transition(CallTransition.PARTICIPANT_JOINED, { participant });
    }

    for (const participant of newParticipantsWithUsername) {
      void transition(CallTransition.PARTICIPANT_USERNAME_TRANSFERRED, {
        participant,
      });
    }

    for (const participant of removedParticipants) {
      void transition(CallTransition.PATIENT_LEFT, { participant });
    }

    lastParticipantsRef.current = callParticipants;
  }, [callParticipants, role]);

  return async (): Promise<boolean> => {
    return callParticipants.length > 0;
  };
};
